import { useEffect } from 'react';

const SITE_URL = 'https://nucleosolucoescontabeis.lovable.app';
const SITE_NAME = 'Núcleo Soluções Contábeis';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.svg`;

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const SEOHead = ({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noindex = false,
  jsonLd,
}: SEOHeadProps) => {
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`;

  useEffect(() => {
    // Title is cheap — set immediately
    document.title = fullTitle;

    // Batch remaining DOM writes after paint via idle callback
    const scheduleUpdate = (cb: () => void) => {
      if ('requestIdleCallback' in window) {
        return requestIdleCallback(cb, { timeout: 500 });
      }
      return setTimeout(cb, 0) as unknown as number;
    };

    const idleId = scheduleUpdate(() => {
      const setMeta = (attr: string, key: string, content: string) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };

      setMeta('name', 'description', description);
      if (noindex) {
        setMeta('name', 'robots', 'noindex, nofollow');
      } else {
        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) robotsMeta.remove();
      }

      setMeta('property', 'og:title', fullTitle);
      setMeta('property', 'og:description', description);
      setMeta('property', 'og:type', ogType);
      setMeta('property', 'og:url', canonicalUrl);
      setMeta('property', 'og:image', ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`);
      setMeta('property', 'og:site_name', SITE_NAME);
      setMeta('property', 'og:locale', 'pt_BR');

      setMeta('name', 'twitter:card', 'summary_large_image');
      setMeta('name', 'twitter:title', fullTitle);
      setMeta('name', 'twitter:description', description);
      setMeta('name', 'twitter:image', ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`);

      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);

      const scriptId = 'seo-json-ld';
      let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (jsonLd) {
        if (!scriptEl) {
          scriptEl = document.createElement('script');
          scriptEl.id = scriptId;
          scriptEl.type = 'application/ld+json';
          document.head.appendChild(scriptEl);
        }
        const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
        scriptEl.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      } else if (scriptEl) {
        scriptEl.remove();
      }
    });

    return () => {
      if ('requestIdleCallback' in window) {
        cancelIdleCallback(idleId as number);
      } else {
        clearTimeout(idleId as unknown as ReturnType<typeof setTimeout>);
      }
      const el = document.getElementById('seo-json-ld');
      if (el) el.remove();
    };
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, noindex, jsonLd]);

  return null;
};

// ─── Schema.org Helpers ───

export const SITE_URL_PUBLIC = SITE_URL;

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'AccountingService',
  name: 'Núcleo Soluções Contábeis',
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  description: 'Economia tributária inteligente para empresas de Lucro Real. Desde 2006 gerando economia para você investir em seu negócio.',
  foundingDate: '2006',
  telephone: '+553132811120',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Av. Raja Gabaglia, 1000',
    addressLocality: 'Belo Horizonte',
    addressRegion: 'MG',
    postalCode: '30380-090',
    addressCountry: 'BR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -19.9391,
    longitude: -43.9532,
  },
  sameAs: [],
  areaServed: {
    '@type': 'Country',
    name: 'Brasil',
  },
  priceRange: '$$',
  serviceType: [
    'Assessoria Contábil',
    'Consultoria Contábil',
    'Redução Tributária',
    'BPO Financeiro',
    'Certificado Digital',
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/blog?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
  })),
});

export const serviceSchema = (opts: {
  name: string;
  description: string;
  url: string;
  serviceType: string;
  areaServed?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: opts.name,
  description: opts.description,
  url: opts.url.startsWith('http') ? opts.url : `${SITE_URL}${opts.url}`,
  serviceType: opts.serviceType,
  provider: {
    '@type': 'AccountingService',
    name: 'Núcleo Soluções Contábeis',
    url: SITE_URL,
  },
  areaServed: {
    '@type': 'Country',
    name: opts.areaServed || 'Brasil',
  },
});

export const faqSchema = (items: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

export const blogListSchema = (posts: { title: string; url: string; date: string; excerpt: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Blog - Núcleo Soluções Contábeis',
  description: 'Artigos sobre contabilidade, tributação, gestão financeira e dicas para empresas de Lucro Real.',
  url: `${SITE_URL}/blog`,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: posts.slice(0, 10).map((post, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: post.url.startsWith('http') ? post.url : `${SITE_URL}${post.url}`,
    })),
  },
});
